import React, { useEffect, useState } from 'react';
import jaJP from 'antd/es/date-picker/locale/ja_JP';
import { Input, Button, Space, Typography, DatePicker, Select, message } from 'antd';
import './index.scss';
import dayjs, { Dayjs } from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import { FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { getListUsers } from '../../../../store/user/userSlice';
import { createNotification } from '../../../../store/notification/notificationSlice';
import { UserInterface } from '../../../../interface/user';
import TableWrap from '../../../../components/TableWrap';
import { NotificationResponse } from '../../../../interface/notification';

const { Text } = Typography;
const PushNotification: React.FC = () => {
  const { data, loading } = useSelector((state: RootState) => state.user);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [notificationContent, setNotificationContent] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sendNotiType, setSendNotiType] = useState('custom');
  const [timeSendType, setTimeSendType] = useState('now');
  const [timeSend, setTimeSend] = useState<Dayjs | null>(null);
  const [isDisable, setDisable] = useState(false);
  const [disableSend, setDisableSend] = useState(true);
  const [filteredData, setFilteredData] = useState<UserInterface[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    try {
      dispatch(getListUsers());
    } catch (error) {
      console.log(`${error}`);
    }
  }, [dispatch]);

  useEffect(() => {
    setFilteredData(data);
    setSearchText('');
  }, [data]);

  useEffect(() => {
    if (sendNotiType === 'all') {
      setDisable(true);
      setSelectedUsers(data.map((user) => user.id));
    } else {
      setDisable(false);
      setSelectedUsers([]);
    }
  }, [data, sendNotiType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData.length]);

  useEffect(() => {
    if (selectedUsers.length === 0) {
      setDisableSend(true);
    } else if (!notificationContent.trim()) {
      setDisableSend(true);
    } else if (timeSendType === 'custom' && !timeSend) {
      setDisableSend(true);
    } else {
      setDisableSend(false);
    }
  }, [notificationContent, selectedUsers.length, timeSend, timeSendType]);

  const columns = [
    {
      title: 'ユーザ名',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <Space>
          <img src={record.avatar} alt="User" className="image-avatar" />
          <Text>{record.name}</Text>
        </Space>
      ),
    },
    {
      title: 'ユーザID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
    },
  ];
  const handleRowSelectionChange = (selectedKeys: React.Key[], selectedRows: UserInterface[]) => {
    const currentPageIds = filteredData
      .slice((currentPage - 1) * pageSize, currentPage * pageSize)
      .map((item) => item.id);

    const remainingSelectedIds = selectedUsers.filter((id) => !currentPageIds.includes(id));

    setSelectedUsers([...remainingSelectedIds, ...selectedKeys.map(String)]);
  };
  const rowSelection = {
    selectedRowKeys: selectedUsers,
    onChange: handleRowSelectionChange,
  };

  const handleSendNotification = async () => {
    const scheduledTimeUnix = timeSend && timeSend.valueOf();
    const payload = {
      deviceId: selectedUsers,
      title,
      body: notificationContent,
      schedule: timeSendType === 'now' ? null : scheduledTimeUnix,
      all: sendNotiType === 'all',
    };
    const result = (await dispatch(createNotification(payload)).unwrap()) as NotificationResponse;
    const { successCount, failureCount, scheduled } = result.data;
    if (scheduled > 0) {
      message.info(`${scheduled}件の通知がスケジュールされました。`);
    }
    if (successCount > 0 && failureCount === 0) {
      message.success(`${successCount}件の通知を正常に送信しました！`);
    } else if (successCount > 0 && failureCount > 0) {
      message.warning(`${successCount}件送信成功、${failureCount}件失敗しました。`);
    } else if (failureCount > 0) {
      message.error(`通知の送信に失敗しました（${failureCount}件）`);
    }
  };
  const handleFilter = () => {
    const searchTerm = searchText.trim().toLowerCase();
    if (!searchTerm) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (user) =>
          (user.name && user.name.toLowerCase().includes(searchTerm)) ||
          (user.email && user.email.toLowerCase().includes(searchTerm)),
      );
      setFilteredData(filtered);
    }
  };

  const handleTimeSendType = (e) => {
    setTimeSendType(e);
    if (e === 'now') {
      setTimeSend(dayjs());
    } else {
      setTimeSend(null);
    }
  };

  return (
    <div>
      <div>
        <div className="input-container">
          <div className="item-label">タイトル</div>
          <div style={{ flex: 1 }}>
            <Input size="large" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="タイトル入力" />
          </div>
        </div>

        <div className="input-container">
          <div className="item-label" style={{ paddingTop: '5px' }}>
            通知内容
          </div>
          <div style={{ flex: 1 }}>
            <TextArea
              size="large"
              placeholder="コンテンツを入力"
              rows={4}
              value={notificationContent}
              onChange={(e) => setNotificationContent(e.target.value)}
            />
          </div>
        </div>

        <div className="input-container">
          <div className="item-label">配信日時</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                size="large"
                value={timeSendType}
                onChange={(e) => handleTimeSendType(e)}
                style={{ width: 140 }}
                suffixIcon={
                  <img src="/assets/icons/arrow_drop_down.png" alt="custom arrow" style={{ width: 24, height: 24 }} />
                }
              >
                <Select.Option value="custom">カスタム</Select.Option>
                <Select.Option value="now">すぐに配信</Select.Option>
              </Select>
              {timeSendType === 'custom' && (
                <DatePicker
                  showTime
                  size="large"
                  locale={jaJP}
                  format="YYYY/MM/DD HH:mm"
                  value={timeSend}
                  onChange={(e) => setTimeSend(e)}
                />
              )}
            </div>
          </div>
        </div>

        <div className="input-container">
          <div className="item-label">配信ユーザー</div>
          <div style={{ flex: 1 }}>
            <Select
              size="large"
              placeholder="カスタム"
              value={sendNotiType}
              onChange={(e) => setSendNotiType(e)}
              style={{ width: 140 }}
              suffixIcon={
                <img src="/assets/icons/arrow_drop_down.png" alt="custom arrow" style={{ width: 24, height: 24 }} />
              }
            >
              <Select.Option value="custom">カスタム</Select.Option>
              <Select.Option value="all">全員</Select.Option>
            </Select>
          </div>
        </div>
      </div>

      <div className="filter-container">
        <div style={{ width: '23%' }}>
          <Input
            placeholder="名前、メールアドレス、電話番号で検索"
            allowClear
            className="search-bar"
            size="large"
            value={searchText}
            disabled={isDisable}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleFilter}
          />
        </div>
        <Button
          icon={<FilterOutlined />}
          type="primary"
          className={isDisable ? 'disabled-button' : 'filter-button'}
          onClick={handleFilter}
          disabled={isDisable}
        >
          フィルタ
        </Button>
      </div>
      <div className="push-notification-container">
        <TableWrap<UserInterface>
          className={isDisable ? 'disabled-table' : 'user-table'}
          rowKey="id"
          columns={columns}
          data={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
          rowSelection={!isDisable ? rowSelection : undefined}
          scroll={filteredData.length > 5 ? { y: 420 } : undefined}
          isLoading={loading}
          total={filteredData.length}
          page={currentPage}
          size={pageSize}
          setPage={(page) => setCurrentPage(page)}
          setSize={(newPageSize) => {
            setPageSize(newPageSize);
            setCurrentPage(1);
          }}
        />
      </div>
      <div>
        <div className="footer">
          <div className="selected-count">選択中: {selectedUsers.length}人</div>
          <Button className="send-button" type="primary" onClick={handleSendNotification} disabled={disableSend}>
            通知送信
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushNotification;
